<template>
  <base-modal
    ref="modalRef"
    :style="cssVars"
    v-bind="$attrs"
    mode="single"
    :title="props.title || 'Select Item'"
    :primary-color="props.primaryColor"
    :clearable="props.clearable"
    :always-clearable="props.alwaysClearable"
    :clear-text="props.clearText || 'Clear'"
    :clear-aria-label="props.clearAriaLabel"
    :width="props.width || 'medium'"
    :height="props.height"
    :show-find="props.showFind"
    :find-placeholder="props.findPlaceholder"
    :auto-focus-find="props.autoFocusFind"
    :show-archived-toggle="props.showArchivedToggle"
    :include-archived="props.includeArchived"
    :show-none-option="props.showNoneOption"
    :none-option-text="props.noneOptionText"
    :none-selected="props.noneSelected"
    :show-row2="props.showRow2"
    :show-footer-controls="props.showFooterControls"
    :show-badge-count="false"
    :show-footer="props.showFooter"
    :primary-action-text="props.primaryActionText"
    :secondary-action-text="props.secondaryActionText"
    :hide-secondary-action="props.hideSecondaryAction"
    :loading="props.loading"
    :disabled="props.disabled"
    :show-loading-overlay="props.showLoadingOverlay"
    :loading-message="props.loadingMessage"
    :body-padding="false"
    :close-on-select="props.closeOnSelect"
    @show="onShow"
    @hide="onHide"
    @shown="onShown"
    @clear="handleClear"
    @search="handleSearch"
    @save="handleSave"
    @cancel="handleCancel"
    @enter-key="handleEnterKey"
    @focus-first="$emit('focus-first')"
    @focus-last="$emit('focus-last')"
    @focus-body-last="$emit('focus-body-last')"
    @archived-toggle-key="$emit('archived-toggle-key', $event)"
    @none-select-key="$emit('none-select-key', $event)"
    @toggle-archived="$emit('toggle-archived', $event)"
    @select-none="$emit('select-none', $event)"
    @navigate-left="$emit('navigate-left')"
    @navigate-right="$emit('navigate-right')"
  >
    <!-- Forward header slots -->
    <template v-if="$slots['header-second-row']" #header-second-row>
      <slot name="header-second-row" />
    </template>
    <template v-if="$slots['header-controls']" #header-controls>
      <slot name="header-controls" />
    </template>

    <!-- Forward custom footer controls slot -->
    <template v-if="$slots['footer-custom-controls']" #footer-custom-controls>
      <slot name="footer-custom-controls" />
    </template>

    <!-- Single column layout -->
    <div class="single-selector-template tw-flex tw-h-full tw-flex-col">
      <!-- Content header (optional) -->
      <div
        v-if="$slots['browser-header'] || props.browserHeaderTitle"
        class="single-selector-template__header tw-shrink-0 tw-border-b tw-px-3 tw-py-2"
      >
        <slot name="browser-header">
          <span class="tw-text-sm tw-font-medium">{{
            props.browserHeaderTitle
          }}</span>
        </slot>
      </div>

      <!-- Navigation breadcrumbs (optional) -->
      <div
        v-if="$slots.navigation"
        class="single-selector-template__navigation tw-shrink-0 tw-border-b tw-border-gray-medium tw-px-3 tw-py-2"
      >
        <slot name="navigation" />
      </div>

      <!-- Filters (optional) -->
      <div
        v-if="$slots.filters"
        class="single-selector-template__filters tw-shrink-0 tw-border-b tw-border-gray-medium tw-p-3"
      >
        <slot name="filters" />
      </div>

      <!-- Content body (scrollable) -->
      <div
        class="single-selector-template__body tw-flex-1 tw-overflow-y-auto"
        @click.stop
      >
        <slot
          name="default"
          :search-query="searchQuery"
          :is-loading="props.loading"
          :selected-item="props.selectedItem"
        />
      </div>

      <!-- Content footer (optional - e.g., toggles, filters) -->
      <div
        v-if="$slots['browser-footer']"
        class="single-selector-template__footer tw-shrink-0 tw-border-t tw-border-gray-medium"
      >
        <slot name="browser-footer" />
      </div>
    </div>
  </base-modal>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts" generic="T extends Record<string, unknown>">
import { ref, computed, watch, nextTick } from 'vue';
import { Colors } from '@/api';
import BaseModal from './BaseModal.vue';
import type { ModalWidth } from '../types';

/**
 * SingleSelectorTemplate - Generic single-selection modal template
 *
 * Provides a standardized single-column layout for any entity selection:
 * - Single pane: browsable list of available items with single selection
 * - Auto-close on selection (optional)
 * - Clear button when item is selected
 * - Fully generic - works with any entity type
 *
 * @example
 * ```vue
 * <single-selector-template
 *   title="Select Item"
 *   :selected-item="selectedItem"
 *   :close-on-select="true"
 *   @save="handleSave"
 *   @search="handleSearch"
 *   @item-selected="handleItemSelected"
 * >
 *   <entity-browser mode="single" />
 *   <template #browser-footer>
 *     <include-archived-toggle />
 *   </template>
 * </single-selector-template>
 * ```
 */

interface Props {
  // Display
  title?: string;
  primaryColor?: Colors;
  width?: ModalWidth;
  height?: number; // Height as vh percentage

  // Selection
  selectedItem?: T | null;
  closeOnSelect?: boolean;

  // Content customization
  browserHeaderTitle?: string;
  browserClasses?: string | string[] | Record<string, boolean>;

  // Header controls
  clearable?: boolean;
  alwaysClearable?: boolean; // When true, clear is always enabled (e.g., for "clear to root" behavior)
  clearText?: string;
  clearAriaLabel?: string;
  showFind?: boolean;
  findPlaceholder?: string;
  autoFocusFind?: boolean;

  // Header Row 3 Controls
  showArchivedToggle?: boolean;
  includeArchived?: boolean;
  showNoneOption?: boolean;
  noneOptionText?: string;
  noneSelected?: boolean;

  // Row Visibility
  showRow2?: boolean;

  // Footer
  showFooterControls?: boolean;
  showFooter?: boolean;
  primaryActionText?: string;
  secondaryActionText?: string;
  hideSecondaryAction?: boolean;

  // State
  loading?: boolean;
  disabled?: boolean;
  showLoadingOverlay?: boolean;
  loadingMessage?: string;
}

interface Emits {
  (e: 'show'): void;
  (e: 'hide'): void;
  (e: 'shown'): void;
  (e: 'clear'): void;
  (e: 'search', query: string, immediate?: boolean): void;
  (e: 'save'): void;
  (e: 'cancel', event?: Event): void;
  (e: 'item-selected', item: T): void;
  (e: 'focus-first'): void;
  (e: 'focus-last'): void;
  (e: 'focus-body-last'): void;
  (e: 'archived-toggle-key', event: KeyboardEvent): void;
  (e: 'none-select-key', event: KeyboardEvent): void;
  (e: 'toggle-archived', value: boolean): void;
  (e: 'select-none', value: boolean): void;
  (e: 'navigate-left'): void;
  (e: 'navigate-right'): void;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  width: 'medium',
  height: 70, // Default to 70vh
  selectedItem: null,
  closeOnSelect: false,
  showFind: true,
  showFooter: true,
  clearable: true,
  autoFocusFind: true,
  hideSecondaryAction: false,
});

const emit = defineEmits<Emits>();

// Computed CSS variables for dynamic theming
const cssVars = computed(() => {
  const primaryColor = `rgb(var(--tw-color-${props.primaryColor}))`;
  return {
    '--scrollbar-color': `color-mix(in srgb, ${primaryColor} 80%, white)`,
  };
});

// Refs
// Type for BaseModal exposed methods
interface BaseModalInstance {
  showPopup: (trigger?: HTMLElement) => void;
  closePopup: () => void;
  focusHeader?: () => void;
  isVisible: boolean;
  updateHasClearableContent?: (hasContent: boolean) => void;
}

const modalRef = ref<BaseModalInstance>();
const searchQuery = ref('');

// Computed
const hasSelection = computed(() => !!props.selectedItem);

// Determine if clear action should be enabled
// When alwaysClearable is true, clear is always available (e.g., "clear to root" behavior)
const hasClearableContent = computed(
  () => props.alwaysClearable || hasSelection.value
);

// Watch for clearable content changes to update modal state
watch(
  hasClearableContent,
  (hasClearable) => {
    modalRef.value?.updateHasClearableContent?.(hasClearable);
  },
  { immediate: true }
);

// Event handlers
async function onShow() {
  emit('show');
  // Update modal state when showing (onShown doesn't fire reliably)
  // Use nextTick to ensure DOM is ready
  await nextTick();
  modalRef.value?.updateHasClearableContent?.(hasClearableContent.value);
}

function onHide() {
  emit('hide');
}

async function onShown() {
  emit('shown');
  // Update modal state after shown (backup for reliability)
  await nextTick();
  modalRef.value?.updateHasClearableContent?.(hasClearableContent.value);
}

function handleClear() {
  // Clear search query when clear button is clicked
  searchQuery.value = '';
  emit('clear');
}

function handleSearch(query: string, immediate?: boolean) {
  searchQuery.value = query;
  emit('search', query, immediate);
}

function handleSave() {
  // Always emit save and let the parent component decide what to do
  // The parent knows better about focused items, selections, etc.
  emit('save');
}

function handleCancel(event?: Event) {
  emit('cancel', event);
}

function handleEnterKey() {
  // Enter key triggers save, same as clicking the save button
  handleSave();
}

// Public API
function showPopup(trigger?: HTMLElement) {
  // Reset search query when opening modal
  searchQuery.value = '';
  modalRef.value?.showPopup(trigger);
}

function closePopup() {
  modalRef.value?.closePopup();
}

function clearSearch() {
  searchQuery.value = '';
}

defineExpose({
  showPopup,
  closePopup,
  clearSearch,
  focusHeader: () => modalRef.value?.focusHeader?.(),
  isVisible: computed(() => modalRef.value?.isVisible || false),
});
</script>

<style lang="scss" scoped>
.single-selector-template {
  // No min-height - flex layout handles sizing
  // The parent modal body constrains the height, and flex children
  // with min-height: 0 will shrink properly to fit

  display: flex;
  flex-direction: column;
  overflow: hidden; // Prevent overflow from children

  &__header {
    @apply tw-bg-gray-light;
    min-height: 36px;
    display: flex;
    align-items: center;
  }

  &__body {
    flex: 1;
    min-height: 0; // Allow flex item to shrink below content size
    overflow-y: auto;
    overscroll-behavior: contain;

    // Optimize scrolling
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      @apply tw-bg-gray-medium;
      border-radius: 3px;
    }
  }

  &__footer {
    @apply tw-bg-gray-light;
  }
}
</style>
